import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import * as modalContentKeys from "Flocknote/GlobalComponents/Modal/contentKeys";
import { useFlocknoteAppState } from "Flocknote/state";

import {
  useMarkAnimated,
  useSetWelcomeIsVisible,
  useWelcomeIsVisible,
} from "Dashboard/components/Greeting/WelcomeToNextFlocknote";
import { nextFlocknoteIsNew } from "Dashboard/components/Greeting/constants";
import { useGetFundList } from "Giving/API/queries";
import { useGroups as useGroupsQuery } from "GroupsManagement/API/queries";
import { useGetSignupsList } from "NavBar/SignupsAndEvents/API/queries";

import defaultObject from "Utilities/defaultObject";
import { fnReportError } from "Utilities/fnReportError";

import { flocknoteKeys } from "./API/keys";
import { useGetNetworkMetadata, useGetPermissions } from "./API/queries";
import flocknoteActions from "./actions";
// TODO: import all instances of this directly from apiHooks
import {
  useFundItStatus,
  useNetworkMetadata,
  usePermissions,
  useUserData,
} from "./apiHooks";
import { mockData } from "./mockData";

const modalContentKeysByValue = Object.fromEntries(
  Object.values(modalContentKeys).map((elem) => [elem, elem])
);

/**
 * Returns true when core app data needed for the NavBar has been fetched.
 * Includes: network metadata, permissions, groups, signups, and funds (if applicable).
 * On /action-center, also waits for the Welcome animation to complete.
 * Use this to delay rendering or animations until the app is ready.
 * @returns {boolean}
 */
const useAppIsReady = () => {
  const { pathname } = useLocation();
  const [{ hasFundIt }] = useNetworkMetadata();
  const { isFetched: metadataFetched } = useGetNetworkMetadata();
  const { isFetched: permissionsFetched } = useGetPermissions();
  const { isFetched: groupsFetched } = useGroupsQuery();
  const { isFetched: signupsFetched } = useGetSignupsList();
  const { isFetched: fundsFetched } = useGetFundList();
  const canOnlyInputGifts = useUserCanOnlyInputGifts();
  const welcomeIsVisible = useWelcomeIsVisible();
  const setWelcomeIsVisible = useSetWelcomeIsVisible();
  const markAnimated = useMarkAnimated();

  // Check if user can see giving/signups sections
  const [
    {
      canViewAllGivingData,
      canAddGifts,
      canSeeCampaignsNav,
      canSeeMMGSetup,
      canSeeSignupsNav,
    },
  ] = usePermissions();
  const canSeeGiving =
    canViewAllGivingData || canAddGifts || canSeeCampaignsNav || canSeeMMGSetup;

  // Giving is ready if: no FundIt, OR funds fetched, OR user can't see giving anyway,
  // OR user can only input gifts (skip the fund-list wait — they don't need it).
  const givingReady =
    !hasFundIt ||
    fundsFetched ||
    (permissionsFetched && (!canSeeGiving || canOnlyInputGifts));

  // Signups is ready if: fetched, OR user can't see signups (query is disabled)
  const signupsReady =
    signupsFetched || (permissionsFetched && !canSeeSignupsNav);

  const isActionCenter = pathname === "/action-center";
  const dataReady =
    metadataFetched &&
    permissionsFetched &&
    groupsFetched &&
    signupsReady &&
    givingReady;

  // Once data is ready on non-action-center routes, mark welcome as visible
  // so navigating to /action-center later won't wait for the animation
  useEffect(() => {
    if (
      nextFlocknoteIsNew &&
      dataReady &&
      !isActionCenter &&
      !welcomeIsVisible
    ) {
      setWelcomeIsVisible(true);
      markAnimated();
    }
  }, [
    dataReady,
    isActionCenter,
    welcomeIsVisible,
    setWelcomeIsVisible,
    markAnimated,
  ]);

  // On action-center, wait for Welcome animation to complete
  const welcomeReady =
    !nextFlocknoteIsNew || !isActionCenter || welcomeIsVisible;

  return dataReady && welcomeReady;
};

const useNetworkDataSlicePatch = (key) => {
  const queryClient = useQueryClient();
  const { data: { [key]: slice } = {} } = useGetNetworkMetadata();
  const patch = useCallback(
    (value) => {
      const queryKey = flocknoteKeys.network();
      const current = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, {
        ...current,
        [key]: value,
      });
      queryClient.invalidateQueries(flocknoteKeys.network());
    },
    [key, queryClient]
  );

  return [slice, patch];
};

/**
 * [getter, setter] for whether or not the network should be sold Flocknote People.
 * @returns {[boolean, function]}
 * @deprecated Use useShowCompleteSplash instead
 */
const useShowPeopleSplash = () => useNetworkDataSlicePatch("showPeopleSplash");

/**
 * [getter, setter] for whether or not the network should be sold Flocknote Complete.
 * @returns {[boolean, function]}
 */
const useShowCompleteSplash = () =>
  useNetworkDataSlicePatch("showCompleteSplash");

/**
 * [getter, setter] for whether or not the network has legacy extended fields available.
 * @returns {[boolean, function]}
 */
const useHasLegacyExtendedFields = () =>
  useNetworkDataSlicePatch("hasLegacyExtendedFields");

/**
 * [getter, setter] for whether or not the network should be sold Flocknote Complete.
 * @returns {[boolean, function]}
 */
const useHasChildNetsWithFNC = () =>
  useNetworkDataSlicePatch("hasChildNetsWithFNC");

/**
 * [getter, setter] for whether or not the network allows networks to enable contact features.
 * @returns {[boolean, function]}
 */
const useCanContactUs = () => useNetworkDataSlicePatch("canContactUs");

/**
 * [getter, setter] for whether or not the network shows the contact menu in the MemberToolbar.
 * @returns {[boolean, function]}
 */
const useHasContactUs = () => useNetworkDataSlicePatch("hasContactUs");

/**
 * [getter, setter] for whether or not the network allows members to submit prayer requests.
 * @returns {[boolean, function]}
 */
const useHasPrayerRequests = () =>
  useNetworkDataSlicePatch("hasPrayerRequests");

/**
 * [getter, setter] for networkLogo url.
 * @returns {[string, function]}
 */
const useNetworkLogo = () => useNetworkDataSlicePatch("networkLogo");

/**
 * [getter, setter] for whether or not the network has used their flocknote people trial.
 * @returns {[boolean, function]}
 * @deprecated Do not use this. FNC Trials no longer exist
 * */
const useHasUsedFPTrial = () => useNetworkDataSlicePatch("hasUsedFPTrial");

/**
 * [getter, setter] for the groupId set to global context by the legacy code.
 * @returns {[number, function]}
 */
const useGroupId = () => {
  const [{ groupId }, dispatch] = useFlocknoteAppState();

  const setGroupId = useCallback(
    (newGroupId) => dispatch(flocknoteActions.setGroupId(newGroupId)),
    [dispatch]
  );

  return [groupId, setGroupId];
};

/**
 * Used to get the background gradient for the network.
 * Returns an array of hex color strings (without # prefix) from global context.
 * @returns {string[]} Array of hex color values like ["720000", "744300", "686E0C"]
 */
const useNetworkBackgroundGradient = () => {
  const [{ backgroundGradient }] = useNetworkMetadata();
  return backgroundGradient;
};

/**
 * [getter, setter] for networkType in global context. Used when network changes their type from the setting sidesheet.
 * @returns {[number, function]}
 */
const useNetworkType = () => useNetworkDataSlicePatch("networkType");

/**
 * [getter, setter] for networkName in global context.
 * @returns {[string, function]}
 */
const useNetworkName = () => useNetworkDataSlicePatch("networkName");

const useNetworkID = () => useNetworkDataSlicePatch("networkID");

const useNetworkIsCatholic = () => useNetworkDataSlicePatch("isCatholic");

const useNetworkIsNonReligious = () =>
  useNetworkDataSlicePatch("isNonReligious");

const useCanHouseholdDatabase = () =>
  useNetworkDataSlicePatch("canHouseholdDatabase");

const useNetworkDenomination = () => {
  const [isCatholic] = useNetworkIsCatholic();
  const [isNonReligious] = useNetworkIsNonReligious();

  if (isNonReligious) return "secular";
  if (isCatholic === false) return "protestant";
  return "catholic";
};

/**
 * [getter, setter] for hasAddOnGifts in global context.
 * @returns {[number, function]}
 */
const useHasAddOnGifts = () => useNetworkDataSlicePatch("hasAddOnGifts");

const useIsClosedNetwork = () => {
  const [networkType] = useNetworkType();
  return networkType === "closed";
};

/**
 * @typedef ModalState
 * @property {boolean} open - is the modal open?
 * @property {string} contentKey - the key to the current modal contents (see src/Flocknote/GlobalComponents/Modal/Modal.js).
 * @property {object} contentProps - the props to be passed to the component being render by the modal.
 */

/**
 * @typedef OpenModal - opens the modal with the given contentKey and contentProps.
 * @type {function}
 * @param {string} contentKey
 * @param {object} contentProps
 */

/**
 * @typedef ModalApi
 * @property {function} closeModal - closes the modal and clears the contentKey and contentProps
 * @property {OpenModal} openModal
 */

/**
 * returns state and interface to interact with the global modal component.
 *
 * @returns {[ModalState, ModalApi]}
 */
const useModal = () => {
  const [
    {
      modal: { isOpen, contentKey, contentProps },
    },
    dispatch,
  ] = useFlocknoteAppState();

  const closeModal = useCallback(() => {
    dispatch(flocknoteActions.closeModal());
  }, [dispatch]);

  const openModal = useCallback(
    // pass in onClose as a special contentProp to fire whatever you need when the modal closes:
    // openModa('someKey',' {onClose: () => {}})
    (newContentKey, newContentProps = {}) => {
      if (modalContentKeysByValue[newContentKey]) {
        dispatch(flocknoteActions.openModal(newContentKey, newContentProps));
      } else {
        fnReportError({
          message: `${newContentKey} was not found in Flocknote/GlobalComponents/Modal/contentKeys. Expected one of:\n\t${Object.values(
            modalContentKeysByValue
          ).join("\n\t")}`,
        });
      }
    },
    [dispatch]
  );

  const modal = {
    isOpen: useMemo(() => isOpen, [isOpen]),
    contentKey: useMemo(() => contentKey, [contentKey]),
    contentProps: useMemo(() => contentProps, [contentProps]),
  };

  return [modal, { closeModal, openModal }];
};

const useUserProfile = () => {
  const [{ userProfile }] = useFlocknoteAppState();

  // TODO: return function to init this state too.
  return [userProfile];
};

const useIsSuperDuper = () => {
  const [{ isSuperDuper }] = useUserData();
  return isSuperDuper;
};

const useUserID = () => {
  const [{ userID }] = useUserData();
  return userID;
};

/** @returns {"full"|"half"|false} */
const useLoginLevel = () => {
  const [{ userID, authorized }] = useUserData();

  if (typeof userID !== "number" || Number.isNaN(userID) || userID <= 0) {
    return false;
  }

  // Note the strict equality check
  if (authorized === false) {
    return "half";
  }

  return "full";
};

/** @returns {boolean} */
const useIsLoggedIn = () => {
  const loginLevel = useLoginLevel();
  return loginLevel === "full";
};

const useUserPhoneInfo = () => {
  const [{ phoneInfo }] = useUserData();
  return phoneInfo || defaultObject;
};

const useUserHasPhone = () => {
  const { phone } = useUserPhoneInfo();
  return Boolean(phone);
};

/**
 * getter and setter for whether or not the network has disabled payment display.
 * @returns {[boolean, function]} */
const useHasHideMakeAPaymentButton = () =>
  useNetworkDataSlicePatch("hideMakeAPaymentButton");

const useCanStax = () => {
  const [{ canStax }] = useNetworkMetadata();
  return canStax;
};

const useIsSuperAdmin = () => {
  const [{ isSuperAdmin }] = useUserData();
  return isSuperAdmin;
};

const useIsSuperOrGroupAdmin = () => {
  const [{ isSuperAdmin, isGroupAdmin }] = useUserData();
  return isSuperAdmin || isGroupAdmin;
};

const useIsBillingAdmin = () => {
  const [{ isBillingAdmin }] = useUserData();
  return isBillingAdmin;
};

const useCanInputGiving = () => {
  const [{ canInputGiving }] = useUserData();
  return canInputGiving;
};

const useIsGroupAdmin = () => {
  const [{ isGroupAdmin }] = useUserData();
  return isGroupAdmin;
};

const useIsRegularUser = () => {
  const isGroupAdmin = useIsGroupAdmin();
  const isSuperAdmin = useIsSuperAdmin();
  const isSuperDuper = useIsSuperDuper();

  return !isGroupAdmin && !isSuperAdmin && !isSuperDuper;
};

const useHasAnyElevatedPermissions = () => {
  const [{ hasAnyElevatedPermissions }] = useUserData();
  return hasAnyElevatedPermissions;
};

const useFunditPricing = () => {
  const [
    {
      value: { pricing },
    },
  ] = useFundItStatus();
  return pricing || defaultObject;
};

const useNoPhoneCodes = () => {
  const [data] = useNetworkMetadata();
  const countryCode = data?.address?.countryCode;

  if (!countryCode) return false;
  return !/^(us|ca)$/i.test(countryCode);
};

const useHasReports = () => {
  const [{ hasReports }] = useNetworkMetadata();
  return hasReports;
};

const useHasFNC = () => {
  const [{ hasFNP }] = useNetworkMetadata();
  return hasFNP;
};

const useHasDiosync = () => {
  const [{ hasDiosync }] = useNetworkMetadata();
  return hasDiosync;
};

const useHasWordOnFireEngage = () => {
  const [{ hasWordOnFireEngage }] = useNetworkMetadata();
  return hasWordOnFireEngage;
};

const useHasFundIt = () => {
  const [{ hasFundIt }] = useNetworkMetadata();
  return hasFundIt;
};

const useHasManageMyGivingPage = () => {
  const [{ hasManageMyGivingPage }] = useNetworkMetadata();
  return hasManageMyGivingPage;
};

const useHasGivingTab = () => {
  const [{ availableProfileTabs }] = useFlocknoteAppState();
  return availableProfileTabs?.some((tab) => tab.action === "giving");
};

/** Stable identity, so a consumer memoizing on the list doesn't rerun each render. */
const NO_ADDITIONAL_GIVING_NETWORKS = [];

/**
 * The networks other than the viewer's own that this household has given to,
 * already filtered to what the viewer may open and already in display order.
 * The legacy giving tab puts it on the bus; the viewer's own network is not in
 * it.
 *
 * @returns {GivingNetwork[]}
 */
const useAdditionalGivingNetworks = () => {
  const [{ userProfile, loadingProfileTab }] = useFlocknoteAppState();

  // Between the url flipping to another household member and that household's
  // giving response landing, the list still describes the household you left.
  if (loadingProfileTab === "giving") return NO_ADDITIONAL_GIVING_NETWORKS;

  if (mockData.shouldMock) return mockData.getAdditionalGivingNetworks();

  return userProfile?.additionalGivingNetworks || NO_ADDITIONAL_GIVING_NETWORKS;
};

/**
 * Whether this household has given to any network other than the viewer's own.
 *
 * Deliberately reads past the in-flight gate above, because open is not the same
 * as populated: a chip whose eligibility came from the gated list would collapse
 * back into a plain button for the length of every reload and then pop back,
 * instead of staying open and empty while the rows it is about to show arrive.
 *
 * @returns {boolean}
 */
const useHasAdditionalGivingNetworks = () => {
  const [{ userProfile }] = useFlocknoteAppState();

  if (mockData.shouldMock)
    return mockData.getAdditionalGivingNetworks().length > 0;

  return Boolean(userProfile?.additionalGivingNetworks?.length);
};

const useShouldInvalidateActiveHouseholdDetail = () => {
  const [{ shouldInvalidateActiveHouseholdDetail }] = useFlocknoteAppState();
  return shouldInvalidateActiveHouseholdDetail;
};

const givingRegExp = new RegExp("give");
const useIsGivingPage = () => {
  const { pathname } = useLocation();

  return useMemo(() => givingRegExp.test(pathname), [pathname]);
};

/**
 * [getter, setter] for whether or not a MemberToolbar is displaying.
 * @returns {[boolean, function]}
 */
const useMemberToolbarDisplaying = () => {
  const [{ isShowingMemberToolbar }, dispatch] = useFlocknoteAppState();

  const setIsShowingMemberToolbar = useCallback(
    (newState) => {
      dispatch(flocknoteActions.setIsShowingMemberToolbar(newState));
    },
    [dispatch]
  );

  return [isShowingMemberToolbar, setIsShowingMemberToolbar];
};

/**
 * [getter, setter] for whether or not a smart group is being made.
 * @returns {[boolean, function]}
 */
const useIsSmartGroupCreation = () => {
  const [{ isSmartGroupCreation }, dispatch] = useFlocknoteAppState();

  const setIsSmartGroupCreation = useCallback(
    (newState) => {
      dispatch(flocknoteActions.setIsSmartGroupCreation(newState));
    },
    [dispatch]
  );

  return [isSmartGroupCreation, setIsSmartGroupCreation];
};

/**
 * [getter, setter] for whether or not the help beacon is open.
 * @returns {[boolean, function]}
 */
const useHelpBeaconIsOpen = () => {
  const [{ helpBeaconIsOpen }, dispatch] = useFlocknoteAppState();

  const setHelpBeaconIsOpen = useCallback(
    (newState) => {
      dispatch(flocknoteActions.setHelpBeaconIsOpen(newState));
    },
    [dispatch]
  );

  return [helpBeaconIsOpen, setHelpBeaconIsOpen];
};

/**
 * [getter, setter] for whether or not the composer is loading.
 * @returns {[boolean, function]}
 */
const useComposerIsLoading = () => {
  const [{ composerIsLoading }, dispatch] = useFlocknoteAppState();

  const setComposerIsLoading = useCallback(
    (newState) => {
      dispatch(flocknoteActions.setComposerIsLoading(newState));
    },
    [dispatch]
  );

  return [composerIsLoading, setComposerIsLoading];
};

/**
 * Whether the user has navigated within the app (vs. arriving directly at a URL).
 * Set by fnReactBus.goToUrl(). Used by settings panels to decide close behavior.
 * @returns {boolean}
 */
const useHasNavigatedInApp = () => {
  const [{ hasNavigatedInApp }] = useFlocknoteAppState();
  return hasNavigatedInApp;
};

const useUserCanOnlyInputGifts = () => {
  const [permissions] = usePermissions();

  return (
    permissions?.canAddGifts &&
    !permissions?.canViewAllGivingData &&
    !permissions?.canSeeCampaignsNav
  );
};

const useUserCanOnlyViewGiving = () => {
  const [permissions] = usePermissions();

  return !!permissions?.canViewAllGivingData && !permissions?.canAddGifts;
};

const flocknoteStateHooks = {
  useShowPeopleSplash,
  useHasUsedFPTrial,
  useGroupId,
  useModal,
  useNetworkType,
  useIsClosedNetwork,
  useHasAddOnGifts,
  useUserProfile,
  useIsSuperOrGroupAdmin,
};

// TODO: do them all like this from now on so auto import is easy.
export {
  useAppIsReady,
  useCanHouseholdDatabase,
  useCanInputGiving,
  useCanStax,
  useFunditPricing,
  useHasChildNetsWithFNC,
  useHasDiosync,
  useHasFNC,
  useHasFundIt,
  useHasManageMyGivingPage,
  useHasGivingTab,
  useAdditionalGivingNetworks,
  useHasAdditionalGivingNetworks,
  useHasHideMakeAPaymentButton,
  useHasLegacyExtendedFields,
  useHasReports,
  useHasWordOnFireEngage,
  useIsBillingAdmin,
  useIsGroupAdmin,
  useHasAnyElevatedPermissions,
  useIsRegularUser,
  useIsSuperDuper,
  useIsSuperAdmin,
  useIsSuperOrGroupAdmin,
  useModal,
  useNetworkBackgroundGradient,
  useNetworkDenomination,
  useNetworkID,
  useNetworkIsCatholic,
  useNetworkIsNonReligious,
  useNetworkName,
  useNoPhoneCodes,
  useShouldInvalidateActiveHouseholdDetail,
  useShowCompleteSplash,
  useUserHasPhone,
  useUserID,
  useUserPhoneInfo,
  useCanContactUs,
  useHasContactUs,
  useHasPrayerRequests,
  useIsLoggedIn,
  useLoginLevel,
  useIsGivingPage,
  useMemberToolbarDisplaying,
  useIsSmartGroupCreation,
  useNetworkLogo,
  useHelpBeaconIsOpen,
  useComposerIsLoading,
  useHasNavigatedInApp,
  useUserCanOnlyInputGifts,
  useUserCanOnlyViewGiving,
};

export default flocknoteStateHooks;
